import { useState, useEffect, useRef, useCallback } from 'react';
import { CallState, StageMode } from '../types';
import { audioService } from '../services/audio';
import { liveWsClient } from '../services/liveWs';
import { runStageViewTransition } from '../utils/viewTransitions';

interface UseLiveCallProps {
  deviceId: string;
  sessionId: string;
  initialStageMode?: StageMode;
  initialStageData?: any;
}

export function useLiveCall({
  deviceId,
  sessionId,
  initialStageMode = 'SPOTLIGHT_GRID',
  initialStageData = null
}: UseLiveCallProps) {
  const [callState, setCallState] = useState<CallState>('idle');
  const [isMuted, setIsMuted] = useState(false);
  const [stageMode, setStageMode] = useState<StageMode>(initialStageMode);
  const [stageData, setStageData] = useState<any>(initialStageData);
  const [spokenCueText, setSpokenCueText] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [waveformHeights, setWaveformHeights] = useState<number[]>([
    4, 6, 8, 12, 16, 14, 10, 8, 12, 16, 12, 8, 4
  ]);

  const animFrameRef = useRef<number | null>(null);
  const inactivityTimerRef = useRef<any>(null);
  const spokenCueClearTimerRef = useRef<any>(null);
  const isStartingRef = useRef<boolean>(false);
  const callAttemptRef = useRef<number>(0);
  const callStateRef = useRef<CallState>('idle');
  callStateRef.current = callState;

  const clearSpokenCueTimer = useCallback(() => {
    if (spokenCueClearTimerRef.current) {
      clearTimeout(spokenCueClearTimerRef.current);
      spokenCueClearTimerRef.current = null;
    }
  }, []);

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    if (callStateRef.current === 'listening') {
      inactivityTimerRef.current = setTimeout(() => {
        if (callStateRef.current === 'listening') {
          callStateRef.current = 'paused_inactivity';
          setCallState('paused_inactivity');
          setIsMuted(true);
          audioService.setMuted(true);
        }
      }, 35000); // 35 seconds of silence
    }
  }, []);

  const handleInterrupt = useCallback(() => {
    audioService.interruptPlayback();
    liveWsClient.sendAudioEnd();
    clearSpokenCueTimer();
    setSpokenCueText('');
    if (callStateRef.current === 'speaking') {
      callStateRef.current = 'listening';
      setCallState('listening');
      resetInactivityTimer();
    }
  }, [resetInactivityTimer, clearSpokenCueTimer]);

  const startCall = useCallback(async () => {
    if (isStartingRef.current || (callStateRef.current !== 'idle' && callStateRef.current !== 'error')) {
      return;
    }
    isStartingRef.current = true;
    const attemptId = ++callAttemptRef.current;

    try {
      setErrorMessage(null);
      callStateRef.current = 'connecting';
      setCallState('connecting');

      audioService.setOnPlaybackStateChange((isPlaying) => {
        if (attemptId !== callAttemptRef.current) return;
        if (!isPlaying && callStateRef.current === 'speaking') {
          callStateRef.current = 'listening';
          setCallState('listening');
          resetInactivityTimer();
          clearSpokenCueTimer();
          spokenCueClearTimerRef.current = setTimeout(() => {
            if (callStateRef.current !== 'speaking') {
              setSpokenCueText('');
            }
          }, 2200);
        }
      });

      audioService.setOnRecordingEnded(() => {
        if (attemptId !== callAttemptRef.current) return;
        if (callStateRef.current !== 'idle' && callStateRef.current !== 'error') {
          setErrorMessage('Microphone disconnected');
          callStateRef.current = 'error';
          setCallState('error');
          audioService.interruptPlayback();
          liveWsClient.close();
        }
      });

      // 1. Acquire microphone & initialize 16kHz PCM recording first within user click gesture
      await audioService.startRecording((pcmChunkBase64) => {
        if (attemptId !== callAttemptRef.current) return;
        liveWsClient.sendAudioChunk(pcmChunkBase64);
      });

      // If endCall() or a newer startCall() superseded this attempt while awaiting getUserMedia, exit without side effects
      if (attemptId !== callAttemptRef.current) {
        return;
      }

      // If audioService.stopRecording() was called directly while awaiting microphone permission, reset to idle cleanly
      if (!audioService.isCurrentlyRecording() || callStateRef.current !== 'connecting') {
        audioService.stopRecording();
        if (callStateRef.current === 'connecting') {
          callStateRef.current = 'idle';
          setCallState('idle');
        }
        return;
      }

      audioService.setMuted(false);
      setIsMuted(false);

      // 2. Connect live WebSocket now that microphone hardware stream is active
      liveWsClient.connect(deviceId, sessionId, {
        onReady: () => {
          if (attemptId !== callAttemptRef.current) return;
          if (callStateRef.current === 'idle' || callStateRef.current === 'error') return;
          callStateRef.current = 'listening';
          setCallState('listening');
          resetInactivityTimer();
        },
        onServerAudio: (pcmBase64) => {
          if (attemptId !== callAttemptRef.current) return;
          if (callStateRef.current === 'idle' || callStateRef.current === 'error') return;
          clearSpokenCueTimer();
          if (callStateRef.current !== 'speaking') {
            callStateRef.current = 'speaking';
            setCallState('speaking');
          }
          if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
            inactivityTimerRef.current = null;
          }
          audioService.queueServerAudio(pcmBase64);
        },
        onSpokenCue: (text) => {
          if (attemptId !== callAttemptRef.current) return;
          clearSpokenCueTimer();
          setSpokenCueText((prev) => `${prev} ${text}`.slice(-320));
        },
        onInterrupted: () => {
          if (attemptId !== callAttemptRef.current) return;
          handleInterrupt();
        },
        onStageUpdate: (mode, data) => {
          if (attemptId !== callAttemptRef.current) return;
          clearSpokenCueTimer();
          setSpokenCueText('');
          const transitionType =
            data?.blueprint?.layoutConfig?.transitionType ||
            (data?.focusView === 'packages' ? 'card-expand' : 'scene-morph');
          runStageViewTransition(() => {
            setStageMode(mode);
            setStageData((prev: any) => {
              if (mode === 'SPOTLIGHT_GRID') {
                return {
                  ...data,
                  stageUpdateAt: Date.now(),
                  spotlightVenues: data?.venues || prev?.spotlightVenues
                };
              }
              return {
                ...prev,
                ...data,
                stageUpdateAt: Date.now(),
                spotlightVenues: prev?.spotlightVenues || prev?.venues,
                venues: prev?.spotlightVenues || prev?.venues || data?.venues,
                compareVenues:
                  mode === 'PRICE_COMPARISON'
                    ? data?.venues || data?.compareVenues
                    : prev?.compareVenues,
                searchFilter:
                  data?.searchFilter !== undefined ? data.searchFilter : prev?.searchFilter
              };
            });
          }, transitionType);
          resetInactivityTimer();
        },
        onSessionUpdate: (_newSessionId, _newDeviceId) => {
          // Sync session
        },
        onError: (err) => {
          if (attemptId !== callAttemptRef.current) return;
          setErrorMessage(err);
          callStateRef.current = 'error';
          setCallState('error');
          audioService.interruptPlayback();
          audioService.stopRecording();
          liveWsClient.close();
        },
        onClose: () => {
          if (attemptId !== callAttemptRef.current) return;
          audioService.interruptPlayback();
          audioService.stopRecording();
          liveWsClient.close();
          if (callStateRef.current !== 'idle' && callStateRef.current !== 'error') {
            callStateRef.current = 'idle';
            setCallState('idle');
          }
        }
      });
    } catch (err: any) {
      if (attemptId !== callAttemptRef.current) {
        return;
      }
      console.error('[useLiveCall] Start call failed:', err);
      setErrorMessage(err?.message || 'Microphone access denied or audio failed');
      callStateRef.current = 'error';
      setCallState('error');
      audioService.stopRecording();
      liveWsClient.close();
    } finally {
      if (attemptId === callAttemptRef.current) {
        isStartingRef.current = false;
      }
    }
  }, [deviceId, sessionId, resetInactivityTimer, handleInterrupt, clearSpokenCueTimer]);

  const endCall = useCallback(() => {
    ++callAttemptRef.current;
    isStartingRef.current = false;
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    clearSpokenCueTimer();
    callStateRef.current = 'idle';
    audioService.interruptPlayback();
    audioService.stopRecording();
    liveWsClient.sendAudioEnd();
    liveWsClient.close();
    setCallState('idle');
    setIsMuted(false);
    setSpokenCueText('');
  }, [clearSpokenCueTimer]);

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => {
      const next = !prev;
      audioService.setMuted(next);
      if (next) {
        liveWsClient.sendAudioEnd();
      }
      return next;
    });
  }, []);

  const resumeFromInactivity = useCallback(() => {
    if (callStateRef.current === 'paused_inactivity' || callState === 'paused_inactivity') {
      audioService.setMuted(false);
      setIsMuted(false);
      callStateRef.current = 'listening';
      setCallState('listening');
      resetInactivityTimer();
    }
  }, [callState, resetInactivityTimer]);

  const sendTouchAction = useCallback((action: string, data?: any) => {
    resetInactivityTimer();
    if (action === 'switch_showcase_focus' && data?.focusView) {
      setStageData((prev: any) => (prev ? { ...prev, focusView: data.focusView } : prev));
    } else if (action === 'swipe_showcase_photo' || action === 'filter_photos') {
      setStageData((prev: any) => (prev ? { ...prev, focusView: 'photos' } : prev));
    }
    liveWsClient.sendClientContent(action, data);
  }, [resetInactivityTimer]);

  // Waveform render loop & global call state sync
  useEffect(() => {
    const isActive = callState !== 'idle' && callState !== 'error';
    window.dispatchEvent(new CustomEvent('mehfil:call-state', { detail: { isCallActive: isActive, callState } }));

    if (callState === 'idle' || callState === 'error' || callState === 'paused_inactivity') {
      setWaveformHeights([4, 6, 8, 12, 16, 14, 10, 8, 12, 16, 12, 8, 4]);
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
        animFrameRef.current = null;
      }
      return;
    }

    let lastUpdateMs = 0;
    const isMobileViewport =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(max-width: 900px), (hover: none) and (pointer: coarse)').matches;
    const minIntervalMs = isMobileViewport ? 48 : 32;

    const updateWaveform = (now: number) => {
      if (now - lastUpdateMs >= minIntervalMs) {
        lastUpdateMs = now;
        const isSpeaking = callState === 'speaking';
        const heights = audioService.getWaveformHeights(isSpeaking);
        setWaveformHeights((prev) => {
          if (
            prev.length === heights.length &&
            prev.every((v, i) => Math.abs(v - heights[i]) < 0.75)
          ) {
            return prev;
          }
          return heights;
        });
      }
      animFrameRef.current = requestAnimationFrame(updateWaveform);
    };

    animFrameRef.current = requestAnimationFrame(updateWaveform);

    return () => {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
        animFrameRef.current = null;
      }
    };
  }, [callState]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      ++callAttemptRef.current;
      isStartingRef.current = false;
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      if (spokenCueClearTimerRef.current) {
        clearTimeout(spokenCueClearTimerRef.current);
      }
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
      }
      audioService.interruptPlayback();
      audioService.stopRecording();
      liveWsClient.close();
    };
  }, []);

  return {
    callState,
    setCallState,
    isCallActive: callState !== 'idle' && callState !== 'error',
    isMuted,
    stageMode,
    stageData,
    spokenCueText,
    setStageMode,
    setStageData,
    errorMessage,
    waveformHeights,
    startCall,
    endCall,
    toggleMute,
    handleInterrupt,
    resumeFromInactivity,
    sendTouchAction
  };
}
